/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications: Added solo variant with CVA; Custom spacing for solo mode
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';
import { OTPInput, OTPInputContext } from 'input-otp';
import { MinusIcon } from 'lucide-react';
import * as React from 'react';

const InputOTPVariantContext = React.createContext<'group' | 'solo'>('group');

type InputOTPProps = React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
  variant?: 'group' | 'solo';
};

const InputOTP = React.forwardRef<React.ElementRef<typeof OTPInput>, InputOTPProps>(
  ({ className, containerClassName, variant = 'group', ...props }, ref) => {
    return (
      <InputOTPVariantContext.Provider value={variant}>
        <OTPInput
          ref={ref}
          data-slot="input-otp"
          containerClassName={cn('flex items-center gap-2 w-full has-disabled:opacity-50', containerClassName)}
          className={cn('disabled:cursor-not-allowed', className)}
          {...props}
        />
      </InputOTPVariantContext.Provider>
    );
  }
);

InputOTP.displayName = 'InputOTP';

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  const variant = React.useContext(InputOTPVariantContext);

  return (
    <div data-slot="input-otp-group" className={cn('flex items-center w-full', variant === 'solo' && 'gap-2', className)} {...props} />
  );
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  index: number;
}) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const variant = React.useContext(InputOTPVariantContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  const slotClassName = cn(
    'bg-card relative flex items-center justify-center text-sm shadow-xs transition-all outline-none',
    'border-input dark:bg-input/30',
    'data-[active=true]:border-ring/65 data-[active=true]:ring-ring/20 data-[active=true]:ring-3 data-[active=true]:z-10',
    'aria-invalid:border-destructive/30 data-[active=true]:aria-invalid:border-destructive',
    'data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40',
    {
      'w-9 h-9 border-y border-r first:rounded-l-md first:border-l last:rounded-r-md': variant === 'group',
      'flex-1 h-14 border rounded-md': variant === 'solo',
    },
    className
  );

  return (
    <div data-slot="input-otp-slot" data-active={isActive} className={slotClassName} {...props}>
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="input-otp-separator" aria-hidden="true" {...props}>
      <MinusIcon />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
